import { useEffect, useRef } from 'react';
import { useEasterEggStore } from './useEasterEggStore';
import { useUpdateUser } from '@services/user/useUpdateUser';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';

/**
 * Syncs the local Easter egg count to the user's displayPreferences on the backend.
 * Debounces writes so we don't PATCH on every single egg click.
 */
export const useEasterEggSync = () => {
  const currentUser = useStore((state) => state.currentUser, shallow);
  const { updateUser } = useUpdateUser({ isEditingOwnProfile: true });
  const eggsByYear = useEasterEggStore((state) => state.eggsByYear);
  const lastSyncedRef = useRef<string>('');
  const timerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    if (!currentUser?.id) return;

    const serialized = JSON.stringify(eggsByYear);
    if (serialized === lastSyncedRef.current) return;

    // Debounce: wait 3 seconds after last egg collection before saving
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      lastSyncedRef.current = serialized;
      const existingPrefs = (currentUser.displayPreferences as Record<string, unknown>) ?? {};
      const existingHolidays = (existingPrefs.holidays as Record<string, unknown>) ?? {};
      updateUser({
        requestBody: {
          displayPreferences: {
            ...existingPrefs,
            holidays: {
              ...existingHolidays,
              easter: {
                eggsByYear,
              },
            },
          },
        },
        requestParams: { userId: currentUser.id },
      }).catch(() => {
        // Silently fail - local state is the source of truth
        // The egg count is preserved in localStorage regardless
      });
    }, 3000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [eggsByYear, updateUser, currentUser]);
};
